import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <footer class="relative bg-polished-charcoal text-silk-white pt-16 pb-12 px-4 sm:px-6 lg:px-8 border-t border-amethyst-glint/25 overflow-hidden shadow-2xl">
      <!-- Glow effect de fondo galáctico sutil -->
      <div class="absolute inset-0 bg-gradient-to-t from-amethyst-dark/90 via-transparent to-transparent pointer-events-none"></div>

      <div class="relative max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 text-sm">

        <!-- Columna 1: Identidad y Logotipo Profesional de Lujo -->
        <div class="space-y-5">
          <div class="flex items-center gap-3.5 group">
            <!-- Emblema Vectorial Profesional de Lujo (SVG Crest) -->
            <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-900/80 via-black to-amethyst-dark border border-amethyst-glint/40 flex items-center justify-center shadow-neon-violet group-hover:scale-105 transition-transform duration-300">
              <svg class="w-7 h-7 text-amethyst-glint" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" fill-opacity="0.15"/>
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                <path d="M12 7V13M9 10L12 13L15 10" stroke-width="1.2"/>
              </svg>
            </div>
            <div>
              <h3 class="text-2xl font-extrabold tracking-wider bg-gradient-to-r from-white via-silk-white to-amethyst-glint bg-clip-text text-transparent font-serif">
                ALTAIR
              </h3>
              <p class="text-[10px] tracking-[0.3em] text-amethyst-glint uppercase font-medium -mt-1">
                Parfum • Luxe
              </p>
            </div>
          </div>

          <p class="text-silk-white/80 leading-relaxed text-sm font-light">
            Alta Perfumería Árabe & Fraccionamiento de Lujo Original. Exclusividad, elegancia y estelas inolvidables en cada decant. ✨
          </p>
          <div class="space-y-1.5 text-xs text-silk-white/60 pt-1">
            <p class="flex items-center gap-2">📍 Bogotá D.C., Colombia</p>
            <p class="flex items-center gap-2">🇨🇴 Despachos seguros a todo el país</p>
          </div>
          
          <!-- Redes Sociales Interactivas -->
          <div class="flex space-x-3 pt-2">
            <a href="https://tiktok.com/@altairparfum" target="_blank" rel="noopener noreferrer" aria-label="TikTok" 
               class="w-10 h-10 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center text-lg hover:border-amethyst-glint hover:scale-110 hover:shadow-neon-violet transition-all duration-300" title="TikTok">
              🎵
            </a>
            <a href="https://instagram.com/altairparfum" target="_blank" rel="noopener noreferrer" aria-label="Instagram" 
               class="w-10 h-10 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center text-lg hover:border-[#e1306c] hover:scale-110 transition-all duration-300" title="Instagram">
              📸
            </a>
            <a href="https://wa.me/573203405747" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" 
               class="w-10 h-10 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center text-lg hover:border-[#25d366] hover:scale-110 transition-all duration-300" title="WhatsApp">
              💬
            </a>
          </div>
        </div>

        <!-- Columna 2: Exploración de Colecciones -->
        <div class="space-y-4">
          <h4 class="text-base font-bold text-amethyst-glint uppercase tracking-widest border-b border-white/10 pb-2">
            Explorar Tienda
          </h4>
          <ul class="space-y-3 text-silk-white/80">
            <li>
              <a routerLink="/tienda" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">✦</span> Tienda Principal
              </a>
            </li>
            <li>
              <a routerLink="/tienda" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">✦</span> Colección Árabe Exclusiva
              </a>
            </li>
            <li>
              <a routerLink="/tienda" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">✦</span> Viales 5ml y 10ml (Decants)
              </a>
            </li>
            <li>
              <a routerLink="/user/login" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">✦</span> Mi Cuenta / Iniciar Sesión
              </a>
            </li>
          </ul>
        </div>

        <!-- Columna 3: Soporte al Consumidor -->
        <div class="space-y-4">
          <h4 class="text-base font-bold text-amethyst-glint uppercase tracking-widest border-b border-white/10 pb-2">
            Soporte & Logística
          </h4>
          <ul class="space-y-3 text-silk-white/80 text-sm">
            <li class="flex items-center gap-2.5">
              <span class="p-1.5 rounded-lg bg-white/5 border border-white/10">📦</span> Cobertura Nacional y Bogotá
            </li>
            <li class="flex items-center gap-2.5">
              <span class="p-1.5 rounded-lg bg-white/5 border border-white/10">💵</span> Pago Contra Entrega Habilitado
            </li>
            <li>
              <a routerLink="/garantias-retracto" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="p-1.5 rounded-lg bg-white/5 border border-white/10">🛡️</span> Garantía Legal y Devoluciones
              </a>
            </li>
            <li class="pt-2">
              <span class="block text-xs font-semibold text-silk-white/50 uppercase tracking-wider mb-1">Contacto Directo SIC:</span>
              <a href="mailto:contacto&#64;altairparfum.com" class="text-white font-medium hover:text-cyan-400 transition block text-sm">
                ✉️ contacto&#64;altairparfum.com
              </a>
            </li>
            <li>
              <a href="https://wa.me/573203405747" target="_blank" rel="noopener noreferrer" class="text-white font-medium hover:text-[#25d366] transition flex items-center gap-1.5 text-sm">
                📞 WhatsApp: +57 320 340 5747
              </a>
            </li>
          </ul>
        </div>

        <!-- Columna 4: Seguridad Jurídica & Cumplimiento -->
        <div class="space-y-4">
          <h4 class="text-base font-bold text-amethyst-glint uppercase tracking-widest border-b border-white/10 pb-2">
            Seguridad Jurídica
          </h4>
          <ul class="space-y-3 text-silk-white/80">
            <li>
              <a routerLink="/terminos" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">⚖️</span> Términos y Condiciones
              </a>
            </li>
            <li>
              <a routerLink="/privacidad" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">🔒</span> Política de Datos (Habeas Data)
              </a>
            </li>
            <li>
              <a routerLink="/garantias-retracto" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">📋</span> Retracto y Excepción Sanitaria
              </a>
            </li>
            <li>
              <a routerLink="/aviso-legal" class="hover:text-white transition-all duration-200 flex items-center gap-2.5 group">
                <span class="text-xs text-amethyst-glint group-hover:translate-x-1 transition-transform">🏛️</span> Aviso Legal de Marcas
              </a>
            </li>
          </ul>
        </div>

      </div>

      <!-- Tarjeta Glassmorphic de Descargo de Marcas y SIC -->
      <div class="relative max-w-7xl mx-auto mt-12 p-6 bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl text-xs text-silk-white/70 leading-relaxed text-justify shadow-inner">
        <p class="font-bold text-white mb-2 uppercase tracking-wider flex items-center gap-2">
          <span>⚖️</span> Aviso de Independencia y Propiedad Intelectual (Normatividad SIC - Colombia):
        </p>
        <p class="font-light">
          En Altair Parfum comercializamos exclusivamente perfumería 100% original. Las marcas, logotipos y nombres de fragancias (tales como <em>Lattafa, Maison Alhambra, Afnan, Armaf</em>, entre otras) pertenecen de manera exclusiva a sus respectivos titulares registrales. Su mención en este sitio web cumple un propósito <strong>estrictamente descriptivo e informativo</strong> para identificar la fragancia original contenida en nuestros viales fraccionados (decants de 5ml y 10ml) y botellas completas. Altair Parfum es un establecimiento de comercio independiente y no posee afiliación, patrocinio ni representación comercial directa con dichos fabricantes.
        </p>
      </div>

      <!-- Copyright & Localización -->
      <div class="relative max-w-7xl mx-auto text-center text-silk-white/50 text-xs mt-10 border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p>© 2026 Altair Parfum (altairparfum.com). Todos los derechos reservados.</p>
        <div class="flex items-center gap-4 text-xs text-silk-white/60">
          <span>🇨🇴 Regulado por la SIC (Ley 1480 / Ley 1581)</span>
          <span>•</span>
          <span>Bogotá D.C., Colombia</span>
        </div>
      </div>
    </footer>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent {}
